import { ArrowLeftIcon } from 'lucide-react'

import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import QuoteFlowHeader from 'components/quoting/QuoteFlowHeader'
import { TooltipProvider } from 'components/ui/tooltip'
import { BorderedRadioButton, Button, RadioGroup, useBreakpoint } from 'slab'
import { useWindowLocation } from 'utils/windowLocation'

import { useEmployer } from '../EmployerProvider'
import { useQuote } from '../QuoteProvider'
import { emitEmployerOnboardingEvent } from '../utils'
import { DynamicQuoteOptions } from './options/DynamicQuoteOptions'
import { FlatQuoteOptions } from './options/FlatQuoteOptions'
import { OutdatedConfigModal } from './OutdatedConfigModal'
import { QuoteCostBottomSheet } from './QuoteCostBottomSheet'
import { QuoteEmployeeTable } from './QuoteEmployeeTable'
import { useSelfServeExplorerContext } from './QuoteSelfServeExplorerContext'
import { QuoteSidebar } from './QuoteSidebar'
import { AllowanceStrategies } from './types'

const SelfServeExplorerImpl = () => {
  const { reload } = useWindowLocation()

  const { employer } = useEmployer()
  const { shouldShowPendingPricesWarning, quote, quoteYear } = useQuote()
  const showWarning = shouldShowPendingPricesWarning && quote?.preliminary
  const companyName = employer?.name ?? 'Your'
  const { allowanceStrategy, setAllowanceStrategy } = useSelfServeExplorerContext()
  const { navigateToPreviousStep } = useOnboardingStep()

  const { breakpointIsAtLeast } = useBreakpoint()
  const { hasError, error } = useSelfServeExplorerContext()

  const title = showWarning
    ? `${companyName}'s allowance strategy (${quoteYear} estimates)`
    : `${companyName} allowance strategy`

  if (hasError) {
    return (
      <div className='h-full w-full flex items-center justify-center text-gray-500 text-md'>
        <div className='text-center'>
          Error loading quote details
          {error?.status === 404 && (
            <div className='mt-4'>
              <Button onClick={() => reload()} variant='primary'>
                Your session may have timed out. Reload?
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    // The bottom margin is to account for the bottom sheet on mobile
    <div className='flex flex-row w-full h-full max-w-[98rem] mx-auto mb-[10rem] md:mb-0'>
      <div className='flex flex-col w-full'>
        <QuoteFlowHeader
          title={title}
          titleSize='xl'
          description=''
          breadcrumb={
            <Button
              variant='tertiary-deemphasized'
              size='small'
              onClick={navigateToPreviousStep}
              style={{ padding: 0 }}
              leftIcon={ArrowLeftIcon}
            >
              Back
            </Button>
          }
        />
        <div className='grid grid-cols-3 gap-10 w-full'>
          <div className='col-span-3 md:col-span-2 space-y-6'>
            <div className='bg-gray-50 rounded-2xl space-y-6 border border-gray-200'>
              <div className='p-6'>
                <RadioGroup name='allowance_strategy' layout='horizontal' gap={3}>
                  <div className='grid md:grid-cols-2 gap-4 w-full'>
                    <BorderedRadioButton
                      value={AllowanceStrategies.Smart}
                      label='Smart allowance'
                      description='Employees receive an amount based on how old they are, where they live, and their number of dependents.'
                      checked={allowanceStrategy === AllowanceStrategies.Smart}
                      onChange={() => {
                        setAllowanceStrategy(AllowanceStrategies.Smart)
                        emitEmployerOnboardingEvent('[ER] AllowanceTool allowance strategy type toggled', {
                          change_to: AllowanceStrategies.Smart
                        })
                      }}
                      size='large'
                    />
                    <BorderedRadioButton
                      value={AllowanceStrategies.Flat}
                      label='Same dollar amount for everyone'
                      description='Every member of your team gets the same dollar amount every month.'
                      checked={allowanceStrategy === AllowanceStrategies.Flat}
                      onChange={() => {
                        setAllowanceStrategy(AllowanceStrategies.Flat)
                        emitEmployerOnboardingEvent('[ER] AllowanceTool allowance strategy type toggled', {
                          change_to: AllowanceStrategies.Flat
                        })
                      }}
                      size='large'
                    />
                  </div>
                </RadioGroup>
              </div>
              {allowanceStrategy === AllowanceStrategies.Smart && (
                <DynamicQuoteOptions />
              )}
              {allowanceStrategy === AllowanceStrategies.Flat && <FlatQuoteOptions />}
            </div>
            <QuoteEmployeeTable />
          </div>
          {breakpointIsAtLeast('md')
            ? (
              <QuoteSidebar />
              )
            : (
              <QuoteCostBottomSheet />
              )}
        </div>
      </div>

      <OutdatedConfigModal quoteYear={quoteYear} />
    </div>
  )
}

export function QuoteSelfServeExplorer () {
  return (
    <TooltipProvider>
      <SelfServeExplorerImpl />
    </TooltipProvider>
  )
}
